import { computed, defineComponent, reactive, ref } from 'vue'
import { MAX_VISIBLE_ITEMS, type SelectDropDownProps, type SelectDropDownState } from './types'
import { onClickOutside } from '@vueuse/core'
import ItemList, { Item } from './components/item-list'
import { clamp } from '@/utils/math'
import type { IInputOption } from '../form/input/types'
import { toString } from '@/utils/formatting'
import { useMemoAsync } from '@/utils/async/useMemoAsync'
import { Spinner } from '../spinner'
import DropdownIcon from 'assets/img/svg/icons/chevron_down.svg?url'
import { tv } from 'tailwind-variants'
import { useInterpolation } from '@/utils/animation/useInterpolation'
import { uniqueBy } from '@/utils/array'
import { FORM_INPUT_HEIGHT_PX } from '../form/constants'
import { unref } from 'vue'
import { getRefId } from 'findef'
import { isNullish } from '@/utils/is'

const clazz = tv({
  base: 'border border-gray-300',
  variants: {
    color: {
      primary: 'border-primary-500 text-primary-500'
    },
    disabled: {
      true: 'opacity-[0.5]'
    },
    noOpacityChangeWhenDisabled: {
      true: ''
    }
  },
  compoundVariants: [
    {
      disabled: true,
      noOpacityChangeWhenDisabled: true,
      class: 'opacity-[1.0]'
    }
  ]
})

export const SelectDropDown = defineComponent<SelectDropDownProps>({
  setup(props) {
    const propValue = computed(() => unref(props.value));

    const state = reactive<SelectDropDownState>(
      (() => {
        //const item = (props.options?.items || []).find((it) => it.value === props.value)
        //const itemText = item ? item.label || item.name || toString(item.value) || '' : undefined
        return {
          value: undefined,
          expanded: props.expanded === true,
          //selectedIndex: item ? (props.options?.items || []).indexOf(item) : -1,
          addedItems: [],
          loading: false,
          searchString: undefined //props.options?.search ? itemText : undefined
        }
      })()
    )

    const interpolation = useInterpolation({
      initial: 0,
      duration: 0.25
    })

    const setExpanded = (expanded: boolean) => {
      if (props.disabled) return
      if (state.expanded === expanded) return

      state.expanded = expanded

      if (expanded) {
        interpolation.runTo(1.0)
      } else {
        interpolation.runTo(0.0)
      }
    }

    const addItem = async (item: IInputOption) => {
      const fieldRef = props.fieldRef
      const dataRef = props.dataRef

      if (fieldRef) {
        if (fieldRef.value.options) {
          const oldItems = fieldRef.value.options.items || []
          fieldRef.value.options.items = uniqueBy([...oldItems, item], (it) => it.value)
        }

        if (props.reportChange) {
          props.reportChange(fieldRef, (item.value || '') as any)
        }
      } else if (dataRef) {
        if ((dataRef as any).options && (dataRef as any).options.value) {
          const oldItems: any[] = (dataRef as any).options.value?.items || ([] as any[]);
          (dataRef as any).options.value.items = uniqueBy([...oldItems, item], (it) => it.value)
        }
        dataRef.setValue(item.value || '')
      }

      ;(state as any).addedItems = [...(state.addedItems as any[]), item]
    }

    const setLoading = (loading: boolean) => {
      state.loading = loading
    }

    const toggleExpanded = () => setExpanded(!state.expanded);

    const optionItems = computed(() => {
      const a = unref(props.options?.computedItems) || [];
      const b = unref(props.options?.items) || [];
      return a.length > 0 ? a : b;
    })

    const loadedItems = useMemoAsync<IInputOption[]>(async (): Promise<IInputOption[]> => {
      let items: IInputOption[] = optionItems.value || []
      const search = props.options?.search

      const fetchAll = search?.api?.fetchAll
      if ((!state.searchString || state.searchString.length <= 1) && fetchAll) {
        let results = await fetchAll()
        
        // Ensure the currently selected value is included in fetchAll results
        const currentValue = unref(propValue);
        if (!isNullish(currentValue) && search?.api?.fetchOne) {
          const currentValueId = getRefId(currentValue) || currentValue;
          const hasCurrentValue = results.some(item => {
            const itemId = getRefId(item.value) || item.value;
            return itemId === currentValueId;
          });
          
          if (!hasCurrentValue) {
            try {
              const selectedOption = await search.api.fetchOne(currentValue);
              if (selectedOption) {
                results = [selectedOption, ...results];
              }
            } catch (error) {
              console.warn('Failed to fetch selected option:', error);
            }
          }
        }
        
        return results
      }

      if (
        state.searchString &&
        state.searchString.length > 1 &&
        state.searchString !== unref(propValue)
      ) {
        if (search && search.api && search.api.fetch) {
          return await search.api.fetch(state.searchString)
        }

        items = items.filter((it) => {
          const text = it.label || it.name || toString(it.value) || ''
          return text.toLowerCase().includes((state.searchString || '').toLowerCase())
        })
      }
      return items
    }, [() => state.searchString])

    const items = computed((): IInputOption[] => {
      if (loadedItems.loading) return []
      let items: IInputOption[] = [
        ...(state.addedItems as any[]),
        ...(loadedItems.data ? loadedItems.data : optionItems.value || [])
      ]
      if (
        state.searchString &&
        state.searchString.length > 1 &&
        state.searchString !== unref(propValue) &&
        !props.options?.search?.api?.fetch
      ) {
        items = items.filter((it) => {
          const text = it.label || it.name || toString(it.value) || ''
          return text.toLowerCase().includes((state.searchString || '').toLowerCase())
        })
      }

      // Ensure the currently selected value is always included (sync only)
      const currentValue = unref(propValue);
      if (!isNullish(currentValue) && props.options?.toOption) {
        const currentValueId = getRefId(currentValue) || currentValue;
        const hasCurrentValue = items.some(item => {
          const itemId = getRefId(item.value) || item.value;
          return itemId === currentValueId;
        });
        
        if (!hasCurrentValue) {
          const selectedOption = props.options.toOption(currentValue);
          if (selectedOption) {
            items.unshift(selectedOption); // Add to beginning
          }
        }
      }

      // Use key-based uniqueness for better object comparison
      return uniqueBy(items, (it) => it.key || getRefId(it.value) || it.value)
      //if (loadedItems.loading) return [];
      //if (!loadedItems.data) return [];
      //console.log({loadedItems});
      //return loadedItems.data;
    })

    const selectedIndex = computed(() => {
      const value = unref(propValue) || state.value
      if (typeof value === 'undefined' || value === null) return -1;
      const idx1 = items.value.map((it) => it.key as string).indexOf(getRefId(value));
      if (idx1 >= 0) return idx1;
      return items.value.map((it) => it.value).indexOf(value) ?? items.value.map((it) => it.key as string).indexOf(getRefId(value))
    });

    const valueIsInvalid = computed(() => {
      const val = unref(propValue);
      if (!val) return false;
      const items = optionItems.value || [];
      if (!items || items.length <= 0) return false;
      return !items.find(it => it.value === val);
    })

    const selectedItem = computed((): IInputOption => {
      if (valueIsInvalid.value && props.config?.invalidValuePlaceholder) return props.config.invalidValuePlaceholder;

      const placeHolderItem = props.small
        ? {
            label: '',
            value: ''
          }
        : {
            label: (props.placeholder || 'Select') + '',
            value: props.placeholder || ''
          }
      if (props.noSelect === true) return placeHolderItem
      if (loadedItems.loading) return placeHolderItem
      if (selectedIndex.value < 0) {
        return placeHolderItem
      }

      if (!items.value) return placeHolderItem
      const its = items.value || []
      if (its.length <= 0) return placeHolderItem
      return its[clamp(selectedIndex.value, 0, its.length - 1)]
    })

    const handleClickItem = (item: IInputOption) => {
      state.value = item.value
      //state.selectedIndex = index
      state.searchString = undefined

      if (props.onValueChange) {
        props.onValueChange(item.value)
      }

      const fieldRef = props.fieldRef
      const dataRef = props.dataRef
      if (fieldRef) {
        //fieldRef.value.value = item.value

        if (props.reportChange) {
          props.reportChange(fieldRef, item.value as any)
        }
      }

      if (dataRef) {
        dataRef.setValue(item.value)
      }

      if (item.action) {
        item.action(item)
      }

      toggleExpanded()
    }

    const handleClickSelectedItem = () => {
      toggleExpanded()
      if (!state.expanded) {
        state.searchString = undefined
      }
    }

    const handleSearchInput = (evt: Event) => {
      const target = evt.target as HTMLInputElement
      const value = target.value
      state.searchString = value
      setExpanded(true)
    }

    const anchor = ref()
    const itemListRef = ref()

    onClickOutside(
      itemListRef,
      () => {
        if (!state.expanded) return
        setExpanded(false)
      },
      { ignore: [anchor] }
    )

    const maxVisibleItems = computed(() => {
      const maxVisible = props.options?.maxVisibleItems || MAX_VISIBLE_ITEMS
      return (
        Math.min(maxVisible, (items.value || []).length) +
        (props.options?.search?.emptyState ? 1 : 0)
      )
    });



    return () => {
      return (
        <div
          class={clazz({ color: props.variant as any, disabled: !!props.disabled, noOpacityChangeWhenDisabled: !!props.noOpacityChangeWhenDisabled })}
          style={{
            position: 'relative',
            width: '100%',
            height: (props.config?.itemHeight ?? FORM_INPUT_HEIGHT_PX) + 'px',
            overflow: 'hidden',
            fontSize: '14px',
            ...(props.disabled
              ? {
                  pointerEvents: 'none'
                }
              : {})
          }}
        >
          <Item
            isValueDisplay
            ref={anchor}
            onClick={handleClickSelectedItem}
            variant={props.variant}
            optimize={false}
            size={props.size}
            selected={false}
            key={`selected-item-${selectedIndex.value}`}
            itemStyle={[props.itemStyleCollapsed, { height: '100%' }]}
            config={props.config}
            small={props.small}
            locked={!!props.disabled || props.locked}
            item={{
              ...(!state.loading && { iconLeft: props.icon }),
              ...selectedItem.value,
              iconRight:
                props.noCollapseIconWhenDisabled && (props.locked || props.disabled)
                  ? undefined
                  : props.config?.collapseIcon
                    ? props.config.collapseIcon
                    : {
                        src: DropdownIcon,
                        size: props.size === 'xs' ? 14 : 14,
                        fill: 'currentColor',
                        flipVert: !!state.expanded
                      },
              ...(loadedItems.loading || state.loading
                ? {
                    label: 'Please wait...',
                    iconLeft: {
                      src: () => <Spinner />,
                      size: 14,
                      fill: 'currentColor'
                    }
                  }
                : {})
            }}
          />
          <ItemList
            selectedIndex={selectedIndex.value}
            config={props.config}
            size={props.size}
            ref={itemListRef}
            onItemClick={handleClickItem}
            itemStyle={props.itemStyleExpanded}
            variant={props.variant}
            items={(items.value || []) as IInputOption[]}
            expanded={state.expanded}
            interpolation={interpolation.state.value}
            anchor={anchor}
            handleSearchInput={handleSearchInput}
            maxVisibleItems={maxVisibleItems.value}
            searchOptions={props.options?.search}
            state={state as any}
            disabled={!!props.disabled || !!props.locked}
            fieldRef={props.fieldRef}
            dataRef={props.dataRef}
            addItem={addItem}
            options={props.options}
            setLoading={setLoading}
            isLoading={state.loading || loadedItems.loading || false}
            minWidth={props.itemListMinWidth}
          />
        </div>
      )
    }
  }
})

SelectDropDown.props = [
  'noSelect',
  'value',
  'size',
  'options',
  'expanded',
  'hiddenWhenCollapsed',
  'fieldRef',
  'dataRef',
  'reportChange',
  'onInput',
  'type',
  'variant',
  'name',
  'locked',
  'disabled',
  'placeholder',
  'label',
  'errors',
  'onValueChange',
  'icon',
  'itemStyleExpanded',
  'itemStyleCollapsed',
  'itemListMinWidth',
  'small',
  'config',
  'noCollapseIconWhenDisabled',
  'noOpacityChangeWhenDisabled'
]

export default SelectDropDown
